/**
 * PillarIdentity — the Identity tile on the Command Center (mockup 01 pillar
 * row). Data: the /userinsights/tenant/{id} summary (UserInsightsSummary).
 *
 * The score is member MFA adoption, the Identity component of the Guardian
 * Score. The visualization is the MFA bar (DESIGN.md "MFA bar"): one bar in
 * two parts, members without MFA in the strong neutral and members with MFA
 * in pt-off, with both counts in the key. It reads the same at 19 members or
 * 4,000 (the Proportional Chart Rule), so it never draws a mark per member.
 * "Global admins" names exactly what `noMfaGlobalAdmins` counts (§7 C3).
 *
 * While Exchange mailbox classification is still running, the member counts
 * and the adoption % include shared and resource mailboxes, so the tile
 * shows the transient refining copy and no score or bar.
 */

import type { ReactNode } from 'react'
import { SegmentedMeter, Skeleton } from '@/components/carbon'
import type { UserInsightsSummary } from '@/types'
import { PillarTile } from './PillarShell'
import { IdentityEmpty } from './EmptyStates'
import { plural } from './cc-queue'

interface Props {
  insights: UserInsightsSummary | null | undefined
  isLoading: boolean
  /** The delta tag ("+2 in 90 days"), from the score history (cc-history.ts). */
  delta?: ReactNode
}

/** Blueprint-specified transient copy (easy-tcm R4/R5): provisioning state, not an error. */
const CLASSIFICATION_REFINING_COPY =
  'user metrics are refining as Exchange finishes setting up.'

const TITLE = 'Identity'
const TO = '/user-insights'
const LINK = 'Open User Analytics'

export default function PillarIdentity({ insights, isLoading, delta }: Props) {
  if (isLoading) {
    return (
      <PillarTile title={TITLE} category="identity" to={TO} linkLabel={LINK} score={null} isLoading finding={<Skeleton lines={2} />} />
    )
  }

  // Empty state: insights null (404 from the API) or hasData=false on the summary.
  if (!insights || !insights.hasData) {
    return (
      <PillarTile title={TITLE} category="identity" to={TO} linkLabel={LINK} score={null} delta={delta}>
        <IdentityEmpty />
      </PillarTile>
    )
  }

  // Strict `=== false` keeps older summaries (undefined) ungated.
  const refining = insights.mailboxClassificationAvailable === false
  if (refining) {
    const finding = `Member counts are on hold: ${CLASSIFICATION_REFINING_COPY}`
    return (
      <PillarTile
        title={TITLE}
        category="identity"
        to={TO}
        linkLabel={LINK}
        score={null}
        finding={finding}
        ariaLabel={`${TITLE}. ${finding}`}
      />
    )
  }

  const total = Math.max(0, insights.enabledMemberUsers ?? 0)
  const without = Math.max(0, insights.noMfaEnabledUsers ?? total - (insights.mfaEnabledMembers ?? 0))
  const withMfa = Math.max(0, total - without)
  const admins = insights.noMfaGlobalAdmins ?? 0
  const score = insights.mfaMemberAdoptionPercentage ?? null

  const finding =
    without > 0
      ? `${without.toLocaleString('en-US')} of ${plural(total, 'member signs', 'members sign')} in without MFA${
          admins > 0 ? `, including ${plural(admins, 'Global admin', 'Global admins')}` : ''
        }.`
      : 'Nothing to review. Every enabled member signs in with MFA.'

  return (
    <PillarTile
      title={TITLE}
      category="identity"
      to={TO}
      linkLabel={LINK}
      score={score}
      delta={delta}
      finding={finding}
      ariaLabel={[score == null ? TITLE : `${TITLE}, ${Math.round(score)}`, finding].join('. ')}
      viz={
        <SegmentedMeter
          caption="Members by MFA"
          segments={[
            { id: 'without', value: without, tone: 'strong', label: 'without MFA' },
            { id: 'with', value: withMfa, tone: 'off', label: 'with MFA' },
          ]}
        />
      }
    />
  )
}
